import React from 'react';
import './Progress.css';

export interface WxbProgressProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'label'> {
  percent: number;
  type?: 'line' | 'circle';
  size?: number;
  showInfo?: boolean;
  status?: 'normal' | 'success' | 'warning' | 'error';
  label?: React.ReactNode;
}

export const WxbProgress: React.FC<WxbProgressProps> = ({
  percent,
  type = 'line',
  size = 80,
  showInfo = true,
  status = 'normal',
  label,
  className = '',
  'aria-label': ariaLabel = '进度',
  ...props
}) => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const value = Number.isFinite(percent) ? Math.max(0, Math.min(100, percent)) : 0;
  const effectiveSize = Number.isFinite(size) ? Math.max(12, size) : 80;
  const radius = (effectiveSize - 6) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;
  const valueText = label ?? `${value}%`;

  React.useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    root.style.setProperty('--wxb-progress-percent', `${value}%`);
    root.style.setProperty('--wxb-progress-size', `${effectiveSize}px`);
  }, [effectiveSize, value]);

  const sharedProps = {
    ...props,
    ref: rootRef,
    role: 'progressbar',
    'aria-label': ariaLabel,
    'aria-valuemin': 0,
    'aria-valuemax': 100,
    'aria-valuenow': value,
    'aria-valuetext': typeof label === 'string' ? label : `${value}%`,
  } as const;

  if (type === 'circle') {
    return (
      <div
        {...sharedProps}
        className={`wxb-progress-circle wxb-progress-status-${status} ${className}`}
      >
        <svg viewBox={`0 0 ${effectiveSize} ${effectiveSize}`} aria-hidden="true" focusable="false">
          <circle
            className="wxb-progress-circle-track"
            cx={effectiveSize / 2}
            cy={effectiveSize / 2}
            r={radius}
            fill="none"
            strokeWidth="5"
          />
          <circle
            className="wxb-progress-circle-bar"
            cx={effectiveSize / 2}
            cy={effectiveSize / 2}
            r={radius}
            fill="none"
            strokeWidth="5"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
          />
        </svg>
        {showInfo && <span className="wxb-progress-circle-text">{valueText}</span>}
      </div>
    );
  }

  return (
    <div
      {...sharedProps}
      className={`wxb-progress-line wxb-progress-status-${status} ${className}`}
    >
      <div className="wxb-progress-track" aria-hidden="true">
        <div className="wxb-progress-bar" />
      </div>
      {showInfo && <span className="wxb-progress-text">{valueText}</span>}
    </div>
  );
};
